import { memo, useState } from 'react'
import { twMerge } from 'tailwind-merge'

interface FaviconItemProps {
  src: string
  zIndex?: number
  className?: string
  showFallback?: boolean
  shadow?: boolean
}

// Deliberately static — favicons render during streaming, and any per-favicon
// mount animation replays on the hot path as metadata churns.
export const FaviconItem = memo(
  ({ src, zIndex = 1, className = '', showFallback = true, shadow = false }: FaviconItemProps) => {
    const [hasError, setHasError] = useState(false)
    const [hasLoaded, setHasLoaded] = useState(false)

    const handleError = () => {
      setHasError(true)
    }

    const handleLoad = () => {
      setHasError(false)
      setHasLoaded(true)
    }

    if (!showFallback) {
      if (hasError) {
        return null
      }

      if (!hasLoaded) {
        return <img src={src} alt="" className="hidden" onError={handleError} onLoad={handleLoad} />
      }
    }

    return (
      <div
        className={twMerge(
          'border-tertiary relative flex items-center justify-center rounded-full border border-2',
          shadow && '[box-shadow:4px_0_8px_rgba(0,0,0,0.3)]',
          className,
        )}
        style={{ zIndex }}
      >
        {showFallback && (!hasLoaded || hasError) ? (
          <>
            <div className="bg-light-10 h-full w-full rounded-full" />
            <img src={src} alt="" className="hidden" onError={handleError} onLoad={handleLoad} />
          </>
        ) : (
          <img
            src={src}
            alt=""
            className="bg-light-15 h-full w-full rounded-full object-cover"
            onError={handleError}
            onLoad={handleLoad}
          />
        )}
      </div>
    )
  },
)

FaviconItem.displayName = 'FaviconItem'
